import axios from "axios";
import {toast} from "react-toastify";
import config from "../constants";

const axiosInstance = axios.create({
    baseURL: config.API_URL,
    timeout: 50000,
    withCredentials: true,
    headers: {
        'Content-Type': 'application/json',
        'accept': 'application/json'
    }
});

// Store CSRF token in memory for cross-domain scenarios
let csrfToken = null;

// Helper function to get CSRF token from cookies (for same-domain scenarios)
const getCsrfTokenFromCookie = () => {
    const name = 'csrftoken';
    let cookieValue = null;
    if (document.cookie && document.cookie !== '') {
        const cookies = document.cookie.split(';');
        for (let i = 0; i < cookies.length; i++) {
            const cookie = cookies[i].trim();
            if (cookie.substring(0, name.length + 1) === (name + '=')) {
                cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
                break;
            }
        }
    }
    return cookieValue;
};

// Request interceptor to add CSRF token to all requests
axiosInstance.interceptors.request.use(
    async (config) => {
        // For non-GET requests, we need a CSRF token
        if (config.method !== 'get' && config.method !== 'head' && config.method !== 'options') {
            // Try to get token from memory first (cross-domain scenario)
            let token = csrfToken;

            // If not in memory, try sessionStorage (set after login)
            if (!token) {
                token = sessionStorage.getItem('csrfToken');
                if (token) {
                    csrfToken = token; // Store in memory for faster access
                }
            }

            // If not in sessionStorage, try to get from cookie (same-domain scenario)
            if (!token) {
                token = getCsrfTokenFromCookie();
            }

            // If we still don't have a token, fetch one from the server
            if (!token) {
                try {
                    const csrfResponse = await axios.get(config.baseURL + '/accounts/csrf/', {
                        withCredentials: true
                    });

                    // Try to get token from response body
                    if (csrfResponse.data && csrfResponse.data.csrfToken) {
                        token = csrfResponse.data.csrfToken;
                    } else if (csrfResponse.data && typeof csrfResponse.data === 'string') {
                        token = csrfResponse.data;
                    }

                    // Store in memory and sessionStorage for future requests
                    if (token) {
                        csrfToken = token;
                        sessionStorage.setItem('csrfToken', token);
                    }
                } catch (error) {
                    console.error('Failed to fetch CSRF token:', error);
                }
            }

            // Add the token to the request headers
            if (token) {
                config.headers['X-CSRFToken'] = token;
            }
        }

        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor to handle errors
axiosInstance.interceptors.response.use(null, async error => {

    if (error.message === 'Network Error') {
        toast.error("An error occurred. Our team has been notified. " +
            "We apologoise for the inconvenience");
        return Promise.reject(error);
    }

    // If we get a CSRF error, clear the stored token and retry once
    if (error.response && error.response.status === 403 &&
        error.response.data && error.response.data.detail &&
        error.response.data.detail.includes('CSRF')) {

        // Clear the stored token
        csrfToken = null;
        sessionStorage.removeItem('csrfToken');

        // Don't retry if this was already a retry
        if (!error.config._retry) {
            error.config._retry = true;

            // Fetch a fresh token
            try {
                const csrfResponse = await axios.get(config.API_URL + '/accounts/csrf/', {
                    withCredentials: true
                });

                if (csrfResponse.data && csrfResponse.data.csrfToken) {
                    csrfToken = csrfResponse.data.csrfToken;
                } else if (csrfResponse.data && typeof csrfResponse.data === 'string') {
                    csrfToken = csrfResponse.data;
                }

                // Store the new token
                if (csrfToken) {
                    sessionStorage.setItem('csrfToken', csrfToken);
                }

                // Retry the original request with the new token
                if (csrfToken) {
                    error.config.headers['X-CSRFToken'] = csrfToken;
                    return axiosInstance.request(error.config);
                }
            } catch (csrfError) {
                console.error('Failed to refresh CSRF token:', csrfError);
            }
        }
    }

    if (error.response && error.response.status === 401) {
        if (window.location.pathname !== '/login') {
            window.location = '/login';
        }
        return Promise.reject(error);
    }

    const expectedError =
        error.response &&
        error.response.status >= 400 &&
        error.response.status < 500;

    if (!expectedError) {
        toast.error("An error occurred. Our team has been notified. " +
            "We apologoise for the inconvenience.");
    }

    return Promise.reject(error);
});

const httpService = {
    get: axiosInstance.get,
    post: axiosInstance.post,
    put: axiosInstance.put,
    patch: axiosInstance.patch,
    delete: axiosInstance.delete,
    options: axiosInstance.options,
}

export default httpService;
